import { useContext, useRef, useEffect } from 'react';
import Newsletter from './Newsletter';
import BlocLinks from './BlocLinks';
import SocialLinks from './SocialLinks';
import { LayoutContext } from '@context/LayoutContext';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { useTranslations } from 'next-intl';
import useI18n from '@hooks/i18n/useI18n';
import { RoutesEnum } from '@monofront/types';
import useLocalizedRoutes from '@hooks/i18n/useLocalizedRoutes';
import FooterCard from './FooterCard';
import { IFooterBlocLinks, IFooterCards } from '@context/LayoutContext/types';
import FooterReinsurance from '../FooterReinsurance';

const Footer = () => {
  const { footer } = useContext(LayoutContext);
  const { isFrenchMarket } = useI18n();
  const collectionsListLinkRef = useRef<HTMLParagraphElement>(null);
  const { getUrl } = useLocalizedRoutes();

  const tGlobal = useTranslations('Global');

  useEffect(() => {
    collectionsListLinkRef.current?.remove();
  }, []);

  const cardList: IFooterCards[] = [];
  const menuColumns: IFooterBlocLinks[] = [];

  if (footer) {
    for (const slice of footer) {
      if (slice.slice_type === 'footer_card') {
        cardList.push(slice as IFooterCards);
      }
      if (slice.slice_type === 'listlink') {
        menuColumns.push(slice as IFooterBlocLinks);
      }
    }
  }

  return (
    <>
      <footer
        id="main-footer"
        className="desktop:tw-px-6 xl:tw-px-0 [&>*]:tw-mx-auto [&>*]:tw-max-w-[1200px]"
        data-testid="main-footer"
      >
        <section className="footer-highlight">
          {cardList.map((slice, index) => (
            <FooterCard key={`footer-card-${index}`} slice={slice}>
              {slice.primary.content === 'newsletter' ? (
                <Newsletter slice={slice} />
              ) : slice.primary.content === 'socialmedia' ? (
                <SocialLinks />
              ) : null}
            </FooterCard>
          ))}
        </section>
        <section className="footer-menu">
          {isFrenchMarket && (
            <div className="footer-menu-download-app">
              <h3 className="accent-title">{tGlobal('downloadApplication')}</h3>
              <a
                href={getUrl(RoutesEnum.APP_STORE_URL)}
                target="_blank"
                rel="noreferrer"
                data-testid="Footer-go-to-desktop"
              >
                <ResponsiveImage
                  alt={tGlobal('downloadApplicationApple')}
                  src="/images/app-mobile/badge-apple-store.png"
                  width={154}
                  height={49}
                />
              </a>
              <br />
              <a
                href={getUrl(RoutesEnum.PLAY_STORE_URL)}
                target="_blank"
                rel="noreferrer"
                data-testid="Footer-div-anchor"
              >
                <ResponsiveImage
                  alt={tGlobal('downloadApplicationGoogle')}
                  src="/images/app-mobile/badge-play-store.png"
                  width={155}
                  height={46}
                />
              </a>
              <div className="qr-code desktop-only">
                <ResponsiveImage
                  alt={tGlobal('downloadApplicationQRcode')}
                  src="/images/app-mobile/laf-qrcode.png"
                  width={100}
                  height={100}
                />
              </div>
            </div>
          )}
          {menuColumns.map((slice, index) => (
            <BlocLinks key={index} slice={slice} />
          ))}
        </section>
        <FooterReinsurance hasAlcoholDisclaimer />
      </footer>
      <style jsx>{`
        footer {
          background-color: var(--clr-primary-5);
          color: var(--black);
          padding-top: var(--space-6);
          padding-bottom: var(--space-6);
        }

        .footer-highlight {
          display: flex;
          justify-content: space-between;
          gap: var(--space-5);
          margin-bottom: var(--space-6);
          padding-bottom: var(--space-6);
          border-bottom: var(--border-separator);
        }

        .footer-highlight > :global(div) {
          flex-grow: 1;
        }

        .footer-menu {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-gap: var(--space-5);
          margin-bottom: var(--space-6);
          padding-bottom: var(--space-6);
          border-bottom: var(--border-separator);
        }

        .footer-menu-download-app h3 {
          margin-bottom: var(--space-5);
        }

        .footer-menu-download-app a {
          display: inline-block;
          margin-bottom: var(--space-2);
        }

        @media (max-width: 800px) {
          footer {
            padding-right: var(--padding-layout-horizontal-sm);
            padding-left: var(--padding-layout-horizontal-sm);
          }

          .footer-highlight {
            display: block;
          }

          .footer-highlight :global(.footer-card:not(:last-child)) {
            margin-bottom: var(--space-5);
          }

          .footer-menu {
            display: block;
            padding-bottom: var(--space-4);
          }

          .footer-menu > :global(div:not(:last-child)) {
            margin-bottom: var(--space-4);
            padding-bottom: var(--space-4);
            border-bottom: var(--border-separator);
          }
        }
      `}</style>
    </>
  );
};

export default Footer;
